import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const SplashScreen = () => {
  const [animationStage, setAnimationStage] = useState(0);
  const navigate = useNavigate();
  // We still need setLocation to update the global context for other parts of the app.
  const { setLocation, isAuthenticated, isAuthLoading } = useAppContext();

  useEffect(() => {
    // --- This is now the ONLY effect hook ---

    // 1. Start visual animations immediately
    const animationTimers = [
      setTimeout(() => setAnimationStage(1), 200),
      setTimeout(() => setAnimationStage(2), 500),
    ];

    const performStartupTasks = async () => {
      const defaultLocation = {
        city: "India",
        pincode: "123000",
        state: "India",
      };
      let finalLocation = defaultLocation;

      // 2. Fetch Location
      try {
        if (!navigator.geolocation) {
          throw new Error("Geolocation is not supported.");
        }

        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            timeout: 10000,
            enableHighAccuracy: true,
          });
        });

        const { latitude, longitude } = position.coords;
        const apiKey = "57df7da3223b453cba2000ca9e3e089f";
        const url = `https://api.opencagedata.com/geocode/v1/json?q=${latitude}+${longitude}&key=${apiKey}`;

        const response = await axios.get(url);
        const result = response.data.results[0];

        if (result?.components) {
          finalLocation = {
            city:
              result.components.city ||
              result.components.town ||
              defaultLocation.city,
            pincode: result.components.postcode || defaultLocation.pincode,
            state: result.components.state_code || defaultLocation.state,
          };
        }
      } catch (error) {
        console.warn("Could not fetch location, using default:", error.message);
        // `finalLocation` is already the default, so no action needed.
      }

      // 3. Update the global context
      setLocation(finalLocation);

      // 4. Wait for authentication check to finish (if it's not already)
      // This is a simple polling mechanism.
      await new Promise((resolve) => {
        const checkAuth = () => {
          if (!isAuthLoading) {
            resolve();
          } else {
            setTimeout(checkAuth, 100); // Check again in 100ms
          }
        };
        checkAuth();
      });

      // 5. All tasks are done. Navigate with the fresh location data.
      const destination = isAuthenticated ? "/app" : "/home";
      navigate(destination, { state: { location: finalLocation } });
    };

    // Give the splash screen a minimum duration before starting async tasks
    const startupTimer = setTimeout(performStartupTasks, 2000);

    return () => {
      clearTimeout(startupTimer);
      animationTimers.forEach(clearTimeout);
    };
  }, [setLocation, isAuthenticated, isAuthLoading, navigate]);

  return (
    <div className="bg-background text-white min-h-screen flex items-center justify-center font-sans selection:bg-white selection:text-black overflow-hidden relative">
       {/* Background Ambience */}
       <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full bg-primary-500/10 blur-[120px] animate-pulse-slow" />
        <div className="absolute bottom-[-20%] left-[-10%] w-[500px] h-[500px] rounded-full bg-secondary-500/10 blur-[120px] animate-pulse-slow delay-1000" />
      </div>

      <div className="flex flex-col items-center justify-center text-center gap-8 relative z-10">
        <AnimatePresence>
          {animationStage >= 1 && (
            <motion.h1
              initial={{ opacity: 0, scale: 0.9, filter: "blur(10px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              className="text-5xl md:text-7xl font-black tracking-tighter uppercase text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-200 to-gray-500"
            >
              TIMEWISE.
            </motion.h1>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {animationStage >= 2 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="flex flex-col items-center gap-4"
            >
              <div className="relative w-16 h-1 bg-white/10 rounded-full overflow-hidden">
                <motion.div 
                  initial={{ x: "-100%" }}
                  animate={{ x: "100%" }}
                  transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-primary-400 to-transparent w-full h-full"
                />
              </div>
              <p className="text-xs text-primary-300/80 uppercase tracking-[0.3em] font-medium">
                Initializing Experience
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.03] z-[100]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />
    </div>
  );
};

export default SplashScreen;
